/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type ICreateClientOpts, MatrixError } from "matrix-js-sdk";
import { logger } from "matrix-js-sdk/lib/logger";

import { Config } from "../config/Config";
import { fallbackICEServerAllowed, initClient } from "./matrix";
import type { InitResult, Session } from "../ClientContext";
import { sessionHomeserver } from "../auth/sessionHomeserver";

export async function initSPA(
  loadSession: () => Session | undefined,
  clearSession: () => void,
  saveRestoredSession?: (session: Session) => void,
): Promise<InitResult | null> {
  // We're running as a standalone application
  const session = loadSession();
  if (!session) {
    logger.log("No session stored; continuing without a client");
    return null;
  }

  logger.log("Using a standalone client");

  /* eslint-disable camelcase */
  const { user_id, device_id, access_token, passwordlessUser } = session;
  const homeserver = sessionHomeserver(
    session,
    Config.defaultHomeserverUrl(),
    Config.defaultServerName(),
  );
  const initClientParams: ICreateClientOpts = {
    baseUrl: homeserver,
    accessToken: access_token,
    userId: user_id,
    deviceId: device_id,
    fallbackICEServerAllowed,
    livekitServiceURL: Config.get().livekit?.livekit_service_url,
  };

  try {
    const client = session.storage_namespace
      ? await initClient(
          initClientParams,
          !session.reset_crypto,
          session.storage_namespace,
        )
      : await initClient(initClientParams, !session.reset_crypto);
    if (session.pending_display_name !== undefined)
      await client.setDisplayName(session.pending_display_name);
    if (
      !session.homeserver_url ||
      session.reset_crypto ||
      session.pending_display_name !== undefined
    ) {
      const updated = { ...session, homeserver_url: homeserver };
      delete updated.reset_crypto;
      delete updated.pending_display_name;
      saveRestoredSession?.(updated);
    }
    return {
      widgetApi: null,
      client,
      passwordlessUser,
    };
  } catch (err) {
    if (err instanceof MatrixError && err.errcode === "M_UNKNOWN_TOKEN") {
      // We can't use this session anymore, so let's log it out
      logger.log(
        "The session from local store is invalid; continuing without a client",
      );
      clearSession();
      // returning null = "no client` pls register" (undefined = "loading" which is the current value when reaching this line)
      return null;
    }
    throw err;
  }
}
